// Shipping Protection App
// Calculates 6% of cart total (excluding protection) and adds matching variant to cart

const CONFIG = {
  PRODUCT_HANDLE: 'shipping-protection-4',
  PERCENTAGE: 0.06,
  MIN_PRICE: 3.00,  // Minimum shipping fee
  MAX_PRICE: 52.00  // Maximum shipping fee
};

let isUpdatingProtection = false; // Flag to prevent recursive updates
let isAddingProtection = false; // Flag to prevent updates while adding
let updateTimeout = null; // Debounce timeout
let messageTimeout = null; // Message display timeout
let currentCartState = null; // Cache current cart state
let originalCartTotal = 0; // Store original cart total for calculations
let userCheckedProtection = false; // Track if user explicitly checked checkbox in this session
let userDisabledProtection = false; // Track if user explicitly disabled protection (don't auto-add)

// Load user preference from sessionStorage
const loadUserPreference = () => {
  try {
    const saved = sessionStorage.getItem('shippingProtectionDisabled');
    return saved === 'true';
  } catch (e) {
    return false;
  }
};

// Save user preference to sessionStorage
const saveUserPreference = (disabled) => {
  try {
    if (disabled) {
      sessionStorage.setItem('shippingProtectionDisabled', 'true');
    } else {
      sessionStorage.removeItem('shippingProtectionDisabled');
    }
  } catch (e) {
    // Ignore storage errors
  }
};

// Initialize from sessionStorage
userDisabledProtection = loadUserPreference();

// Floor amount to nearest .5 or .0
const floorToHalf = (amount) => {
  const whole = Math.floor(amount);
  const decimal = amount - whole;
  return decimal < 0.5 ? whole : whole + 0.5;
};

// Format currency
const formatMoney = (amount, currency) => {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// Update cart total display in real-time
const updateCartTotal = (newTotal, currency) => {
  // Update main cart total
  const cartTotalElements = document.querySelectorAll('.cart-total, .cart__total, [data-cart-total], .totals__subtotal-value, .cart-subtotal, .totals__total-value ');
  cartTotalElements.forEach(element => {
    if (element) {
      element.textContent = formatMoney(newTotal, currency) + " " + currency;
    }
  });
  
  // Update mini cart total
  const miniCartTotalElements = document.querySelectorAll('.cart-drawer__total, .drawer__total, [data-drawer-total], .cart-count-bubble__total');
  miniCartTotalElements.forEach(element => {
    if (element) {
      element.textContent = formatMoney(newTotal, currency);
    }
  });
  
  // Update any other total displays
  const totalElements = document.querySelectorAll('[data-total], .total-amount, .cart-total-amount');
  totalElements.forEach(element => {
    if (element && element.textContent.includes('$') || element.textContent.includes('€') || element.textContent.includes('£')) {
      element.textContent = formatMoney(newTotal, currency);
    }
  });
};

// Update estimated shipping time display
const updateShippingTime = (hasProtection) => {
  const shippingTimeElements = document.querySelectorAll('.shipping-time, .estimated-delivery, .delivery-time, [data-shipping-time]');
  shippingTimeElements.forEach(element => {
    if (element) {
      if (hasProtection) {
        element.textContent = '1-2 business days';
        element.classList.add('protected-shipping');
      } else {
        element.textContent = '3-5 business days';
        element.classList.remove('protected-shipping');
      }
    }
  });
};

// Show message on both cart page and mini cart
const showMessage = (message, temporary = false) => {
  const displays = document.querySelectorAll('.protection-amount-display');
  displays.forEach(display => {
    display.textContent = message;
  });
  
  // Clear existing timeout
  if (messageTimeout) {
    clearTimeout(messageTimeout);
    messageTimeout = null;
  }
  
  // If temporary, show price after delay
  if (temporary) {
    messageTimeout = setTimeout(() => {
      const protection = window.shippingProtectionCalculation;
      if (protection) {
        showMessage(`Added protection for ${protection.formatted}`);
      }
    }, 2000);
  }
};

// Calculate 6% protection fee (excluding existing protection and virtual products) with min/max constraints
const calculateProtectionFee = (cart, variants) => {
  let subtotal = cart.total_price;
  
  cart.items.forEach(item => {
    if (item.handle !== CONFIG.PRODUCT_HANDLE && !requiresShipping(item)) {
      subtotal -= item.final_line_price;
    }
  });
  
  const protectionItem = cart.items.find(item => item.handle === CONFIG.PRODUCT_HANDLE);
  if (protectionItem) {
    subtotal -= protectionItem.final_line_price;
  }
  
  const cartTotal = subtotal / 100;
  const protectionAmount = cartTotal * CONFIG.PERCENTAGE;
  const flooredAmount = floorToHalf(protectionAmount);
  const currency = cart.currency || 'USD';
  
  
  // Apply min/max constraints
  const constrainedAmount = Math.max(
    CONFIG.MIN_PRICE,
    Math.min(flooredAmount, CONFIG.MAX_PRICE)
  );
  
  // Find matching variant within constraints
  let selectedVariant = null;
  if (variants && variants.length > 0) {
    const sortedVariants = [...variants].sort((a, b) => a.price - b.price);
    
    // Find exact match or closest lower price within constraints
    selectedVariant = sortedVariants.find(v => v.price / 100 === constrainedAmount) ||
                     sortedVariants.find(v => v.price / 100 <= constrainedAmount) ||
                     sortedVariants[0];
  }
  
  return {
    amount: constrainedAmount,
    displayAmount: constrainedAmount,
    formatted: formatMoney(constrainedAmount, currency),
    currency: currency,
    variant: selectedVariant,
    subtotal: cartTotal
  };
};

// Find matching variant by price
const findVariant = (targetAmount, variants) => {
  if (!variants || variants.length === 0) return null;
  
  const sorted = [...variants].sort((a, b) => a.price - b.price);
  const highest = sorted[sorted.length - 1];
  const highestPrice = highest.price / 100;
  
  if (targetAmount > highestPrice) return highest;
  
  const targetCents = Math.round(targetAmount * 100);
  const exactMatch = sorted.find(v => v.price === targetCents);
  if (exactMatch) return exactMatch;
  
  const targetStr = targetAmount.toFixed(2);
  const titleMatch = sorted.find(v => {
    const title = v.title.trim().toLowerCase();
    return title === targetStr || title === `$${targetStr}`;
  });
  if (titleMatch) return titleMatch;
  
  let closest = sorted[0];
  for (const variant of sorted) {
    if (variant.price / 100 <= targetAmount) {
      closest = variant;
    } else {
      break;
    }
  }
  
  return closest;
};

// Add variant to cart (without reload)
const addToCart = (variantId) => {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(response => {
    if (!response.ok) throw new Error('Failed to add to cart');
    return response.json();
  })
  .then(item => {
    // Fetch fresh cart data to get accurate total
    return fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        // Update cart state with fresh data
        currentCartState = cart;
        
        // Update visibility classes on shipping protection sections
        updateShippingProtectionVisibility(cart);
        
        // Update cart total with fresh cart data
        const newTotal = cart.total_price / 100;
        updateCartTotal(newTotal, cart.currency);
        updateShippingTime(true);
        
        return item;
      });
  });
};

// Remove protection from cart (without reload)
const removeFromCart = (itemKey) => {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: itemKey, quantity: 0 })
  })
  .then(response => response.json())
  .then(cart => {
    // Update cart state with fresh data
    currentCartState = cart;
    
    // Update visibility classes on shipping protection sections
    updateShippingProtectionVisibility(cart);
    
    // Immediately update cart total after removal using fresh cart data
    const newTotal = cart.total_price / 100;
    updateCartTotal(newTotal, cart.currency);
    updateShippingTime(false);
    
    return cart;
  });
};

// Sync empty state for both cart page and mini cart
const syncCartEmptyState = () => {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      if (typeof shouldShowCartEmptyState !== 'function') return;
      
      const shouldShowEmpty = shouldShowCartEmptyState(cart);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      const cartItems = document.querySelector('cart-items');
      const cartFooter = document.getElementById('main-cart-footer');
      
      if (cartDrawerWrapper) {
        cartDrawerWrapper.classList.toggle('is-empty', shouldShowEmpty);
        const drawerHeader = cartDrawerWrapper.querySelector('.drawer__header');
        const drawerFooter = cartDrawerWrapper.querySelector('.drawer__footer');
        const drawerInnerEmpty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
        
        if (drawerHeader) drawerHeader.style.display = shouldShowEmpty ? 'none' : '';
        if (drawerFooter) drawerFooter.style.display = shouldShowEmpty ? 'none' : '';
        if (drawerInnerEmpty) drawerInnerEmpty.style.display = shouldShowEmpty ? '' : 'none';
      }
      if (cartDrawerItems) {
        cartDrawerItems.classList.toggle('is-empty', shouldShowEmpty);
      }
      if (cartItems) {
        cartItems.classList.toggle('is-empty', shouldShowEmpty);
      }
      if (cartFooter) {
        cartFooter.classList.toggle('is-empty', shouldShowEmpty);
      }
    })
    .catch(() => {});
};

// Lightweight cart UI refresh - no content reloading
const refreshCartUI = () => {
  // Trigger Shopify's pub/sub cart update
  if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shipping-protection' });
  }
  
  // Trigger cart items refresh directly
  const cartItems = document.querySelector('cart-items');
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  
  if (cartItems && typeof cartItems.onCartUpdate === 'function') {
    cartItems.onCartUpdate();
  }
  
  if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
    cartDrawerItems.onCartUpdate();
  }
  
  // Dispatch custom events for compatibility
  document.dispatchEvent(new CustomEvent('cart:refresh'));
  document.dispatchEvent(new CustomEvent('cart:updated'));
  
  // Sync empty state after refresh
  setTimeout(syncCartEmptyState, 100);
};

// Update protection variant in cart
const updateProtectionVariant = (oldItemKey, newVariantId) => {
  isUpdatingProtection = true;
  
  // Remove old variant and add new one
  return removeFromCart(oldItemKey)
    .then(() => addToCart(newVariantId))
    .then((newItem) => {
      // Cart total is already updated by addToCart using fresh data
      // Trigger cart refresh
      setTimeout(() => {
        refreshCartUI();
        isUpdatingProtection = false;
      }, 100);
    })
    .catch(() => {
      isUpdatingProtection = false;
    });
};

// Check if protection is in cart
const isInCart = (cart) => {
  return cart.items.some(item => item.handle === CONFIG.PRODUCT_HANDLE);
};

// Check if item requires shipping (not virtual)
const requiresShipping = (item) => {
  
  return item.requires_shipping !== false;
};

// Get items that require shipping (excluding protection and virtual products)
const getShippingRequiredItems = (cart) => {
  const allItems = cart.items.map(item => ({
    title: item.title || item.product_title,
    handle: item.handle,
    requires_shipping: item.requires_shipping,
    final_line_price: item.final_line_price,
    isProtection: item.handle === CONFIG.PRODUCT_HANDLE
  }));
  
  
  const shippingItems = cart.items.filter(item => 
    item.handle !== CONFIG.PRODUCT_HANDLE && requiresShipping(item)
  );
  
  const shippingItemsDebug = shippingItems.map(item => ({
    title: item.title || item.product_title,
    handle: item.handle,
    requires_shipping: item.requires_shipping,
    final_line_price: item.final_line_price
  }));
  
  
  return shippingItems;
};

// Get non-protection item count (actual products in cart that require shipping)
const getNonProtectionItemCount = (cart) => {
  return getShippingRequiredItems(cart).length;
};

// Check if cart has any items that require shipping
const hasShippingRequiredItems = (cart) => {
  const shippingItems = getShippingRequiredItems(cart);
  const hasItems = shippingItems.length > 0;
  return hasItems;
};

// Toggle classes on shipping protection sections based on virtual products
const updateShippingProtectionVisibility = (cart) => {
  const hasShippingItems = hasShippingRequiredItems(cart);
  const shippingProtectionSections = document.querySelectorAll('.shipping-product-app-block.cart-drawer-shipping');
  
  shippingProtectionSections.forEach(section => {
    // Hide only when cart has ONLY virtual products (no shipping items)
    section.classList.toggle('hide-for-virtual-only', !hasShippingItems);
  });
};



// Handle cart changes and update protection if needed (with debounce)
const handleCartUpdate = () => {
  if (isUpdatingProtection || isAddingProtection) return; // Don't update during protection changes
  
  // Clear previous timeout
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }
  
  // Immediate check for auto-add (no delay for first-time adding protection)
  const product = window.shippingProtectionData;
  if (product && !userDisabledProtection) {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const hasShippingItems = hasShippingRequiredItems(cart);
        const protectionInCart = isInCart(cart);
        
        // Update visibility classes on shipping protection sections
        updateShippingProtectionVisibility(cart);
        
        // If cart has shipping-required items but no protection, add it immediately (no delay)
        if (!protectionInCart && hasShippingItems && !isAddingProtection) {
          isAddingProtection = true;
          const protection = calculateProtectionFee(cart, product.variants);
          window.shippingProtectionCalculation = protection;
          
          showMessage('Adding protection for ' + protection.formatted + '...');
          
          const variant = findVariant(protection.amount, product.variants);
          if (variant) {
            addToCart(variant.id)
              .then(() => {
                showMessage('Added protection for ' + protection.formatted, true);
                const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
                checkboxes.forEach(checkbox => {
                  checkbox.checked = true;
                });
                refreshCartUI();
                isAddingProtection = false;
              })
              .catch(() => {
                showMessage(`${protection.formatted}`);
                isAddingProtection = false;
              });
          } else {
            isAddingProtection = false;
          }
        }
      })
      .catch(() => {
        // Continue with debounced update on error
      });
  }
  
  // Show "cart is being updated" message immediately
  showMessage('Cart is being updated...');
  
  // Wait 3 seconds for cart to fully update before recalculating
  updateTimeout = setTimeout(() => {
    const product = window.shippingProtectionData;
    const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
    
    if (!product || checkboxes.length === 0) return;
    
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        currentCartState = cart; // Cache cart state
        const protectionItem = cart.items.find(item => item.handle === CONFIG.PRODUCT_HANDLE);
        const nonProtectionCount = getNonProtectionItemCount(cart);
        const protectionInCart = isInCart(cart);
        
        // Check if cart has any items that require shipping
        const hasShippingItems = hasShippingRequiredItems(cart);
        
        // Update visibility classes on shipping protection sections
        updateShippingProtectionVisibility(cart);
        
        // Update checkbox state based on cart state
        checkboxes.forEach(checkbox => {
          // Checkbox is checked if protection is in cart AND cart has shipping-required items
          // If cart has no shipping-required items (only virtual products), checkbox is unchecked and disabled
          checkbox.checked = protectionInCart && hasShippingItems;
          checkbox.disabled = !hasShippingItems;
        });
        
        // Update cart total with fresh cart data first (important for quantity updates)
        const newTotal = cart.total_price / 100;
        updateCartTotal(newTotal, cart.currency);
        
        // If cart has no shipping-required items (only virtual products or empty), 
        // ensure protection is removed and checkbox is unchecked
        // Also reset user preference when cart becomes empty (fresh start)
        if (!hasShippingItems) {
          // Reset preference when cart is empty (fresh start)
          userDisabledProtection = false;
          saveUserPreference(false);
          
          if (protectionInCart && protectionItem) {
            removeFromCart(protectionItem.key)
              .then(() => {
                checkboxes.forEach(checkbox => {
                  checkbox.checked = false;
                });
                refreshCartUI();
              })
              .catch(() => {
                checkboxes.forEach(checkbox => {
                  checkbox.checked = false;
                });
                // Still update total even on error
                fetch('/cart.js')
                  .then(r => r.json())
                  .then(updatedCart => {
                    currentCartState = updatedCart;
                    const total = updatedCart.total_price / 100;
                    updateCartTotal(total, updatedCart.currency);
                  });
              });
            return; // Exit early since we're removing protection
          }
        }
        
        // Calculate new protection fee
        const newProtection = calculateProtectionFee(cart, product.variants);
        window.shippingProtectionCalculation = newProtection;
        
        // Update display with new amount
        showMessage(`${newProtection.formatted}`);
        
        // Update shipping time based on current state
        updateShippingTime(protectionInCart);
        
        // If protection is in cart, check if it needs updating (only if there are shipping-required items)
        if (protectionItem && hasShippingItems) {
          const currentVariantPrice = protectionItem.price / 100;
          const newVariant = findVariant(newProtection.amount, product.variants);
          
          // Update if the variant changed
          if (newVariant && newVariant.price / 100 !== currentVariantPrice) {
            showMessage('Updating the shipping protection fee...');
            updateProtectionVariant(protectionItem.key, newVariant.id)
              .then(() => {
                showMessage(`${newProtection.formatted}`);
              })
              .catch(() => {
                showMessage(`${newProtection.formatted}`);
              });
          }
        } else if (!protectionInCart && hasShippingItems && !userDisabledProtection && !isAddingProtection) {
          // Protection is not in cart but cart has items - add it automatically (default enabled)
          // Only auto-add if user hasn't explicitly disabled it and we're not already adding
          isAddingProtection = true;
          showMessage('Adding protection for ' + newProtection.formatted + '...');
          
          const variant = findVariant(newProtection.amount, product.variants);
          if (variant) {
            addToCart(variant.id)
              .then(() => {
                showMessage('Added protection for ' + newProtection.formatted, true);
                // Update checkbox state to checked
                checkboxes.forEach(checkbox => {
                  checkbox.checked = true;
                });
                refreshCartUI();
                isAddingProtection = false;
              })
              .catch(() => {
                showMessage(`${newProtection.formatted}`);
                isAddingProtection = false;
              });
          } else {
            isAddingProtection = false;
          }
        }
      })
      .catch(() => {
        // Restore previous display on error
        if (window.shippingProtectionCalculation) {
          showMessage(`${window.shippingProtectionCalculation.formatted}`);
        }
      });
  }, 1500);
};

// Setup checkbox event handler - immediate and smooth
const setupCheckbox = (checkbox, product, protection) => {
  checkbox.addEventListener('change', function() {
    // Mark that user explicitly interacted with checkbox
    userCheckedProtection = this.checked;
    
    // Track if user explicitly disabled protection
    if (!this.checked) {
      userDisabledProtection = true; // User explicitly disabled, don't auto-add
      saveUserPreference(true); // Save to sessionStorage
    } else {
      userDisabledProtection = false; // User enabled it, allow auto-add again
      saveUserPreference(false); // Clear from sessionStorage
    }
    
    // Sync all checkboxes immediately for smooth UX
    const allCheckboxes = document.querySelectorAll('.shipping-product-checkbox');
    allCheckboxes.forEach(cb => cb.checked = this.checked);
    
    if (this.checked) {
      // Add protection immediately
      isAddingProtection = true;
      showMessage('Adding protection for ' + window.shippingProtectionCalculation.formatted + '...');
      
      const variant = findVariant(window.shippingProtectionCalculation?.amount || protection.amount, product.variants);
      if (variant) {
        addToCart(variant.id)
          .then(() => {
            showMessage('Added protection for ' + window.shippingProtectionCalculation.formatted, true);
            refreshCartUI();
            isAddingProtection = false;
          })
          .catch(() => {
            // Revert checkboxes on error
            allCheckboxes.forEach(cb => cb.checked = false);
            const currentCalc = window.shippingProtectionCalculation || protection;
            showMessage(`${currentCalc.formatted}`);
            isAddingProtection = false;
          });
      } else {
        // Revert checkboxes if no variant found
        allCheckboxes.forEach(cb => cb.checked = false);
        isAddingProtection = false;
      }
    } else {
      // Remove protection immediately when user unchecks
      isUpdatingProtection = true;
      showMessage('Removing...');
      
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const item = cart.items.find(i => i.handle === CONFIG.PRODUCT_HANDLE);
          if (!item) {
            isUpdatingProtection = false;
            userCheckedProtection = false; // Reset flag
            return;
          }
          
          return removeFromCart(item.key)
            .then(() => {
              const currentCalc = window.shippingProtectionCalculation || protection;
              showMessage(`${currentCalc.formatted}`);
              userCheckedProtection = false; // Reset flag when user unchecks
              refreshCartUI();
              isUpdatingProtection = false;
            });
        })
        .catch(() => {
          // Revert checkboxes on error
          allCheckboxes.forEach(cb => cb.checked = true);
          const currentCalc = window.shippingProtectionCalculation || protection;
          showMessage(`${currentCalc.formatted}`);
          isUpdatingProtection = false;
        });
    }
  });
};

// Track initialized checkboxes to avoid duplicate listeners
const initializedCheckboxes = new WeakSet();

// Setup checkboxes helper
const setupAllCheckboxes = () => {
  const product = window.shippingProtectionData;
  const protection = window.shippingProtectionCalculation;
  
  if (!product || !protection) return;
  
  const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
  
  checkboxes.forEach(checkbox => {
    // Skip if already initialized
    if (initializedCheckboxes.has(checkbox)) return;
    
    // Mark as initialized
    initializedCheckboxes.add(checkbox);
    
    // Setup event handler
    setupCheckbox(checkbox, product, protection);
  });
};

// Initialize app
const initShippingProtection = () => {
  // Check visibility immediately on page load (before delay)
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      updateShippingProtectionVisibility(cart);
    })
    .catch(() => {
      // On error, show shipping protection by default
    });
  
  // Show "cart is being updated" message on page load
  showMessage('Cart is being updated...');
  
  // Wait 1.5 seconds for cart to fully load and stabilize
  setTimeout(() => {
    Promise.all([
      fetch(`/products/${CONFIG.PRODUCT_HANDLE}.js`).then(r => r.json()),
      fetch('/cart.js').then(r => r.json())
    ])
    .then(([product, cart]) => {
    // Store product data
    window.shippingProtectionData = product;
    
    // Store current cart state and original total
    currentCartState = cart;
    originalCartTotal = cart.total_price / 100;
    
    // Check if cart has any items that require shipping
    const hasShippingItems = hasShippingRequiredItems(cart);
    
    // Update visibility of shipping protection sections
    updateShippingProtectionVisibility(cart);
    
    // Calculate protection fee (only on shipping-required items)
    const protection = calculateProtectionFee(cart, product.variants);
    window.shippingProtectionCalculation = protection;
    
    const nonProtectionCount = getNonProtectionItemCount(cart);
    const protectionInCart = isInCart(cart);
    
    // Update display
    showMessage(`${protection.formatted}`);
    
    // Update shipping time based on current state
    updateShippingTime(protectionInCart);
    
    // Setup all checkboxes (cart page and mini cart)
    const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
    if (checkboxes.length === 0) return;
    
    // Setup checkboxes - state matches cart state
    checkboxes.forEach(checkbox => {
      // Set initial state based on cart:
      // - If protection is in cart and cart has shipping-required items: checked
      // - If cart has no shipping-required items (only virtual products): unchecked (and disabled)
      // - If cart has shipping-required items but no protection: checked (default enabled)
      checkbox.checked = protectionInCart && hasShippingItems;
      checkbox.disabled = !hasShippingItems;
      
      // If protection is in cart, mark that user has it enabled (so it persists)
      if (protectionInCart && hasShippingItems) {
        userCheckedProtection = true;
      }
      
      // Setup event handler
      setupCheckbox(checkbox, product, protection);
      initializedCheckboxes.add(checkbox);
    });
    
    // If cart has shipping-required items but no protection, add it automatically (default enabled)
    // Only auto-add if user hasn't explicitly disabled it
    if (hasShippingItems && !protectionInCart && !userDisabledProtection) {
      isAddingProtection = true;
      showMessage('Adding protection for ' + protection.formatted + '...');
      
      const variant = findVariant(protection.amount, product.variants);
      if (variant) {
        addToCart(variant.id)
          .then(() => {
            showMessage('Added protection for ' + protection.formatted, true);
            // Update checkbox state to checked
            checkboxes.forEach(checkbox => {
              checkbox.checked = true;
            });
            userCheckedProtection = true; // Mark as enabled
            refreshCartUI();
            isAddingProtection = false;
          })
          .catch(() => {
            showMessage(`${protection.formatted}`);
            isAddingProtection = false;
          });
      } else {
        isAddingProtection = false;
      }
    }
    
    // If cart has no shipping-required items (only virtual products or empty), reset preference (fresh start)
    if (!hasShippingItems) {
      userDisabledProtection = false;
      saveUserPreference(false);
      
      // If cart has no shipping-required items but protection is still in cart, remove it
      if (protectionInCart) {
        const protectionItem = cart.items.find(item => item.handle === CONFIG.PRODUCT_HANDLE);
        if (protectionItem) {
          // Remove protection when cart has no shipping-required items (async, don't block setup)
          removeFromCart(protectionItem.key)
            .then(() => {
              // Update checkboxes to unchecked
              const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
              checkboxes.forEach(checkbox => {
                checkbox.checked = false;
              });
              userCheckedProtection = false; // Reset flag when cart is cleared
              refreshCartUI();
            })
            .catch(() => {
              // On error, still set checkboxes to unchecked
              const checkboxes = document.querySelectorAll('.shipping-product-checkbox');
              checkboxes.forEach(checkbox => {
                checkbox.checked = false;
              });
              userCheckedProtection = false;
            });
        }
      }
    }
    
    // Listen for cart updates (from other products)
    document.addEventListener('cart:refresh', () => {
      handleCartUpdate(); // Debounced update
      syncCartEmptyState(); // Sync empty state
    });
    document.addEventListener('cart:updated', () => {
      handleCartUpdate(); // Debounced update
      syncCartEmptyState(); // Sync empty state
    });
    
    // Also listen for Shopify cart drawer events
    if (typeof Shopify !== 'undefined') {
      document.addEventListener('cart-drawer:refresh', () => {
        handleCartUpdate();
      });
      document.addEventListener('shopify:section:load', () => {
        handleCartUpdate();
      });
    }
    
    // Listen for pub/sub cart updates
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'shipping-protection') {
          // If event has cart data, update totals immediately (for quantity updates)
          if (event.cartData) {
            // parsedState from cart.js has items, total_price, etc.
            const cartData = event.cartData;
            
            // Update cart state if we have the data
            if (cartData.items !== undefined || cartData.total_price !== undefined) {
              const cart = {
                items: cartData.items || currentCartState?.items || [],
                total_price: cartData.total_price !== undefined ? cartData.total_price : (currentCartState?.total_price || 0),
                currency: cartData.currency || currentCartState?.currency || 'USD'
              };
              currentCartState = cart;
              
              // Update cart total immediately for quantity changes
              if (cartData.total_price !== undefined) {
                const newTotal = cartData.total_price / 100;
                updateCartTotal(newTotal, cart.currency);
              }
            }
          }
          
          handleCartUpdate(); // Debounced update for protection recalculation
        }
        // Re-setup checkboxes after cart update
        setTimeout(setupAllCheckboxes, 200);
      });
    }
    
    })
    .catch(() => {
      showMessage('Error loading protection');
    });
  }, 1500);
};

// Expose init function globally for re-initialization
window.initShippingProtection = initShippingProtection;

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShippingProtection);
} else {
  initShippingProtection();
}
// Re-initialize when cart drawer opens (for dynamically loaded content)
document.addEventListener('cart-drawer:open', () => {
  setTimeout(() => {
    initShippingProtection();
    fetch('/cart.js').then(r => r.json()).then(updateShippingProtectionVisibility);
  }, 100);
});
// Re-initialize when cart drawer content is refreshed
document.addEventListener('cart-drawer:refresh', () => {
  setTimeout(() => {
    initShippingProtection();
    fetch('/cart.js').then(r => r.json()).then(updateShippingProtectionVisibility);
  }, 200);
});


